import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TouchableOpacity } from 'react-native';

import { EntryDetails } from '@/api/entries/types/types';
import { CloseIcon } from '@/assets/icons/close';
import InfoIcon from '@/assets/icons/info.js';
import StrikedThroughDotLarge from '@/assets/icons/striked-through-dot-large';
import { ds } from '@/components/lib/design.system';
import { isEntryWin } from '@/feature/entries-pickem/entries-utils';
import { getWarningBannerExpireTime } from '@/feature/entries-pickem/entries-warning-utils';
import { useEntriesWarningStore } from '@/feature/entries-pickem/hooks/use-entries-warning-store';
import { useRescueBannerInfo } from '@/hooks/use-rescue-banner-payout';
import { useReviveBannerInfo } from '@/hooks/use-revive-banner-payout';
import { common } from '@/styles/styles';
import { Result, Status } from '@/types/api.generated';

import { Text } from './TextComponent';
import { Box, Row } from './lib/components';

const styles = StyleSheet.create({
    root: {
        backgroundColor: ds.colorValue.background.neutral.subtler,
        borderRadius: 16,
        paddingVertical: 14,
        paddingHorizontal: 16,
        gap: 16,
        alignItems: 'center',
    },
});

export const EntryWarning = ({
    entry,
    voidedPicksCount,
    newMultiplier,
    initialMultiplier,
    hasRevivedPicks,
    hasRescuedPicks,
}: {
    entry?: EntryDetails;
    voidedPicksCount?: number;
    newMultiplier: string;
    initialMultiplier: string;
    hasRevivedPicks?: boolean;
    hasRescuedPicks?: boolean;
}) => {
    const { t } = useTranslation(['betslip_pickem', 'numbers']);
    const [showMultiplierUpdateBanner, setShowMultiplierUpdateBanner] = useState(false);

    const addToEntryWarningBanners = useEntriesWarningStore(state => state.actions.addToEntryWarningBanners);
    const closedEntryWarningBanners = useEntriesWarningStore(state => state.closedEntryWarningBanners);
    const {
        title: bannerTitle,
        description: bannerDescription,
        enabled: isReviveBannerEnabled,
    } = useReviveBannerInfo();

    const {
        title: rescueBannerTitle,
        description: rescueBannerDescription,
        enabled: isRescueBannerEnabled,
    } = useRescueBannerInfo();

    useEffect(() => {
        //we don't want to display the warning banner for this entry if we have it in the storage (that means the close button was pressed)
        if (entry?.id && closedEntryWarningBanners[entry.id]) {
            return;
        }

        // In case we didn't press the close button on the banner we check if the entry is settled (win/loss) or if the banner expiration time didn't pass current time
        const currentTime = new Date().getTime();
        const settlementDateWithExtraTime = getWarningBannerExpireTime(entry?.settlementDate);
        const shouldShowMultiplierUpdateBanner =
            entry?.status === Status.Settled &&
            (isEntryWin(entry) || entry?.result === Result.Loss) &&
            currentTime < settlementDateWithExtraTime;
        setShowMultiplierUpdateBanner(shouldShowMultiplierUpdateBanner);
    }, [entry, closedEntryWarningBanners]);

    const closePopup = () => {
        setShowMultiplierUpdateBanner(false);
        if (entry?.id) {
            const expireTime = getWarningBannerExpireTime(entry.settlementDate);
            addToEntryWarningBanners(entry.id, expireTime);
        }
    };

    // If we have revived picks and the entry is a win we show the revived picks payout banner
    // If we have rescued picks and the entry is a win we show the rescued picks payout banner
    // If the entry is voided we show the voided entry warning banner
    // If we have voided picks and the entry is settled win/loss we show the multiplier update banner
    // otherwise we don't show any banner
    const isResultWin = isEntryWin(entry);
    const shouldShowBannerPayoutInfo = entry?.id && closedEntryWarningBanners[entry.id] === undefined;
    const showReviveBanner = hasRevivedPicks && isResultWin && isReviveBannerEnabled && shouldShowBannerPayoutInfo;
    const showRescuedBanner = hasRescuedPicks && isResultWin && isRescueBannerEnabled && shouldShowBannerPayoutInfo;
    return showReviveBanner ? (
        <Row style={styles.root}>
            <InfoIcon width={24} height={24} color={ds.colorValue.icon.primary} />
            <Box style={common.flex}>
                <Text variant="buttonLabel">{bannerTitle}</Text>
                <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                    {bannerDescription}
                </Text>
            </Box>
            <TouchableOpacity style={common.justifyCenter} onPress={closePopup} hitSlop={12}>
                <CloseIcon fillColor={ds.colorValue.icon.primary} width={16} height={16} />
            </TouchableOpacity>
        </Row>
    ) : showRescuedBanner ? (
        <Row style={styles.root}>
            <InfoIcon width={24} height={24} color={ds.colorValue.icon.primary} />
            <Box style={common.flex}>
                <Text variant="buttonLabel">{rescueBannerTitle}</Text>
                <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                    {rescueBannerDescription}
                </Text>
            </Box>
            <TouchableOpacity style={common.justifyCenter} onPress={closePopup} hitSlop={12}>
                <CloseIcon fillColor={ds.colorValue.icon.primary} width={16} height={16} />
            </TouchableOpacity>
        </Row>
    ) : entry?.result === Result.Void ? (
        <Row style={styles.root}>
            <Box justifyContent={'center'}>
                <StrikedThroughDotLarge fillColor={ds.colorValue.icon.primary} />
            </Box>
            <Box flex={1}>
                <Text variant="bodySmall">{t('voided_entry_warning')}</Text>
            </Box>
        </Row>
    ) : voidedPicksCount && showMultiplierUpdateBanner ? (
        <Row style={styles.root}>
            <Box style={common.flex}>
                <Text variant="buttonLabel">{t('multiplier_updated')}</Text>
                <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                    {t('multiplier_updated_warning', {
                        number: voidedPicksCount,
                        count: voidedPicksCount,
                        initialMultiplier: initialMultiplier,
                        newMultiplier: newMultiplier,
                    })}
                </Text>
            </Box>
            <TouchableOpacity style={common.justifyCenter} onPress={closePopup} hitSlop={12}>
                <CloseIcon fillColor={ds.colorValue.icon.primary} width={16} height={16} />
            </TouchableOpacity>
        </Row>
    ) : null;
};
